import { z } from 'zod'

export const SourceSchema = z.object({
    clientCode: z.string().max(10),
    channelCode: z.string().max(10),
    systemCode: z.string().max(10),
    journeyCode: z.string().max(10),
    userId: z.string().max(50),
    interactionId: z.string().max(100),
    interactionDirection: z.enum(['I', 'O']).optional(),
    interactionEventDateTime: z.string().optional(),
    interactionIdSystemCode: z.enum(['GCO']).optional(),
})

export const SourceCodeSchema = SourceSchema.pick({
    clientCode: true,
    channelCode: true,
    systemCode: true,
    journeyCode: true,
})

export type SourceType = z.infer<typeof SourceSchema>
export type SourceCodeType = z.infer<typeof SourceCodeSchema>

const offerSchema = z.object({
    ref: z.string().describe('Offer reference'),
    priceOptionId: z.array(z.number()),
    key: z.number().describe('This is the offer ID'),
})

export const offerAttributesSchema = z.object({
    productVariantId: z.string().describe('Unique ID for the product variant'),
    itemIterationId: z
        .number()
        .describe(
            'Unique ID for the item iteration, must link to the item iteration id inside of the items array which is also present in the POST contract payload',
        ),
    netTotalAmount: z.number(),
    taxTotalAmount: z.number(),
    netFirstPaymentAmount: z.number(),
    taxRate: z.number().describe('Effective tax rate'),
    periodOfCover: z.number().describe('Period of cover in months'),
    levelOfCoverCode: z
        .string()
        .describe('Code representing the level of cover'),
    paymentProfile: z.object({
        code: z.string().describe('Example: CC12MTHLYW0'),
        paymentMethod: z.string().describe('Example: ACH'),
    }),
    discountProfile: z
        .object({
            code: z
                .string()
                .describe('D&G code of the discount profile, example: D20PTM'),
            term: z
                .number()
                .describe(
                    'Term of the discount (number of months), this is based on the discountProfile.code. We must work out ourselves how long the term is and pass this along',
                ),
            amount: z.number().describe('The amount of the discount.'),
            type: z
                .enum(['P', 'V'])
                .describe(
                    'Defines if discount amount is a percentage or monetary value. P: Percentage V:Value',
                ),
            undiscountedNetTotalAmount: z
                .number()
                .describe(
                    'This will be the original netTotalAmount for the contracts full period, before any discounts have been applied',
                ),
        })
        .optional()
        .describe(
            'Discount profile details, for create contract SDK we WILL be passing this when operating in MODE 2, which is direct offer takeup with override',
        ),
    effectiveDate: z
        .string()
        .date()
        .optional()
        .default(new Date().toISOString().split('T')[0])
        .describe(
            'Defines a future date for the contract to start (optional).',
        ),
    thirdPartyContractExpiryDate: z
        .string()
        .date()
        .optional()
        .describe(
            'If the customer has a third party contract covering the item, when it expires',
        ),
    offer: offerSchema.pick({
        ref: true,
        priceOptionId: true,
    }),
})

const contractOfferAttributesSchema = z.object({
    isMultiplan: z.boolean(),
    attributes: z.array(offerAttributesSchema),
})

export type ContractAttributesType = z.infer<
    typeof contractOfferAttributesSchema
>

const directOfferTakeupSchema = z.object({
    offers: z.array(
        z.object({
            offer: offerSchema.pick({
                ref: true,
                priceOptionId: true,
            }),
        }),
    ),
})

export type DirectOfferTakeupSchemaType = z.infer<
    typeof directOfferTakeupSchema
>

const personSchema = z.object({
    personId: z.string().max(100),
})

export const itemSchema = z.object({
    itemIterationId: z.number(),
    item: z.object({
        itemId: z.string(),
    }),
})

export type ItemSchemaType = z.infer<typeof itemSchema>

const billingSchema = z.object({
    id: z
        .string()
        .max(150)
        .describe(
            'Billing account id (paysupervisor billing account id), returned when calling POST billing to create a new billing account.',
        ),
    type: z.string().max(50).optional(),
    profileId: z
        .string()
        .max(150)
        .describe(
            'Billing profile id (paysupervisor profile id), This is the id returned when calling PUT billing to create a profile for an existing billing account.',
        ),
})

const IpsCreateContractRequestSchema = z
    .object({
        source: SourceSchema,
        billing: billingSchema,
    })
    .and(
        z.union([
            z
                .object({
                    person: personSchema,
                    items: z.array(itemSchema),
                    contract: contractOfferAttributesSchema,
                })
                .describe(
                    'Direct Offer takeup with a contract override, this is what we will use for create contract sdk flow.',
                ),
            z
                .object({
                    contract: directOfferTakeupSchema,
                })
                .describe(
                    'Direct Offer takeup, this will most likely be used for the PCIPAL telephony flow',
                ),
        ]),
    )

type IpsCreateContractRequestType = z.infer<
    typeof IpsCreateContractRequestSchema
>

// https://domgen-api-portal.gh.domgencloud.net/d-g-ips-core-apis-external-v2-1.0.0/#tag/Contract/operation/createContract
const IpsCreateContractResponseSchema = z.object({
    personId: z.string().max(100),
    contract: z.array(
        z.object({
            itemIterationId: z
                .number()
                .optional()
                .describe(
                    'A unique ID for each item in array - required if isMultiplan=false',
                ),
            itemTypeCode: z.string().max(10).optional(),
            offer: z
                .object({
                    ref: z.string().max(100).describe('Offer reference'),
                    key: z.number().describe('This is the offer id').optional(),
                    priceOptionId: z
                        .number()
                        .describe('This is what we call Offer pricing key')
                        .optional(),
                })
                .optional(),
            created: z
                .object({
                    id: z
                        .string()
                        .max(100)
                        .describe('D&G Contract Unique Identifer'),
                    effectiveDate: z.string().optional(),
                    renewalDate: z.string().optional(),
                    expirationDate: z.string().optional(),
                    netTotalAmount: z.number().optional(),
                    taxTotalAmount: z.number().optional(),
                    grossTotalAmount: z.number().optional(),
                })
                .or(
                    z.object({
                        errorDescription: z.string(),
                    }),
                ),
        }),
    ),
})

type IpsCreateContractResponseType = z.infer<
    typeof IpsCreateContractResponseSchema
>

export {
    IpsCreateContractResponseType,
    IpsCreateContractResponseSchema,
    IpsCreateContractRequestType,
    IpsCreateContractRequestSchema,
}
